import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
/**
 * Query keys are exported so a mutation elsewhere can invalidate precisely
 * what it changed instead of blowing away the whole cache.
 */
export const queryKeys = {
    vehicles: (query = {}) => ['vehicles', query],
    vehicle: (idOrSlug) => ['vehicle', idOrSlug],
    bookings: (query = {}) => ['bookings', query],
    booking: (reference) => ['booking', reference],
    threads: (query = {}) => ['threads', query],
    thread: (id) => ['thread', id],
    unreadMessages: () => ['threads', 'unread'],
    attachmentLink: (id) => ['thread-attachment', id],
    threadMetrics: (range = {}) => ['thread-metrics', range],
    /*
     * Deliberately not under `threads`: the directory and the canned
     * replies are read *beside* an inbox and are not part of it, and a send
     * that invalidates `['threads']` must not throw them away with it.
     */
    staff: (query = {}) => ['staff', query],
    savedReplies: (query = {}) => ['saved-replies', query],
    /*
     * Both under `notifications`, so invalidating the prefix moves the feed
     * and the dot on the bell together — which is always what you want: a
     * list that has grown a row and a count that has not is the bug.
     */
    notifications: (query = {}) => ['notifications', query],
    unreadNotifications: () => ['notifications', 'unread'],
};
/*
 * ── Threads ────────────────────────────────────────────────
 *
 * **An open conversation is streamed; everything around it still polls.**
 * That split is deliberate rather than half-finished. A thread on screen is
 * where promptness is the product, and it is also where polling cost the
 * most — the whole transcript, every few seconds, mostly unchanged. An
 * inbox and a badge are glanced at, and a stream each would be a socket
 * each for information nobody is waiting on.
 *
 * The sequence number is what made the swap cheap: these hooks already
 * treated "what has arrived" as a question with a cursor rather than as a
 * list that happens to be fresh, so nothing above them had to change.
 */
/** How often an inbox asks whether anything was said in any of its threads. */
const INBOX_POLL_MS = 20_000;
/** The nav badge, which nobody is staring at. */
const UNREAD_POLL_MS = 60_000;
export function useThreads(client, query = {}) {
    return useQuery({
        queryKey: queryKeys.threads(query),
        queryFn: () => client.listThreads(query),
        refetchInterval: INBOX_POLL_MS,
        /*
          Whatever the data's age. Coming back to the app is the moment
          somebody checks whether they were answered — often because a banner
          just told them so — and a list under `staleTime` stays as it was
          until the next poll, up to twenty seconds behind the banner.
        */
        refetchOnWindowFocus: 'always',
    });
}
export function useUnreadMessages(client, enabled = true) {
    return useQuery({
        queryKey: queryKeys.unreadMessages(),
        queryFn: () => client.unreadMessages(),
        refetchInterval: UNREAD_POLL_MS,
        // With the inbox, so the badge and the list it counts never disagree.
        refetchOnWindowFocus: 'always',
        enabled,
    });
}
/**
 * One conversation, kept current, and marked as read while it is open.
 *
 * The read cursor is moved from here rather than from a button, because
 * "read" means the messages were on the screen — and a thread the reader
 * is looking at while replies arrive should not accumulate a badge behind
 * its own scroll position.
 */
export function useThread(client, id) {
    const queryClient = useQueryClient();
    const query = useQuery({
        queryKey: queryKeys.thread(id ?? ''),
        queryFn: () => client.thread(id),
        enabled: Boolean(id),
        /*
          Read once, then listened to. This used to poll the whole conversation
          every five seconds — every message, every attachment, twelve times a
          minute, for a thread that had usually not changed — and a screen that
          replaces its data that often is a screen that flickers.
        */
        refetchInterval: false,
    });
    const seq = query.data?.messageSeq ?? 0;
    const unread = query.data?.unreadCount ?? 0;
    /*
      The cursor the stream resumes from, held in a ref rather than read off
      the query: the effect below must not tear the connection down and open
      a new one every time a message arrives, which is exactly what naming
      `messageSeq` as a dependency would do.
    */
    const cursor = useRef(0);
    cursor.current = Math.max(cursor.current, seq);
    /** True once the thread has been read, so the stream has a cursor to start from. */
    const ready = Boolean(id) && query.isSuccess;
    useEffect(() => {
        if (!id || !ready)
            return;
        // Built inside the effect, where it is a value rather than a dependency.
        const key = queryKeys.thread(id);
        let live = true;
        let close = null;
        let retry = null;
        /*
          Backed off, doubling to a minute. A server that has just fallen over
          does not need every open thread reconnecting to it twice a second,
          and a person reading a conversation will not notice the difference
          between a reconnect now and one in four seconds.
        */
        let wait = 1_000;
        /* Set once the first connection has dropped; see `onClose`. */
        let reconnected = false;
        const open = () => {
            if (!live)
                return;
            if (reconnected)
                void queryClient.invalidateQueries({ queryKey: key });
            close = client.streamThread(id, {
                onMessage: (message) => {
                    cursor.current = Math.max(cursor.current, message.seq);
                    queryClient.setQueryData(key, (thread) => {
                        if (!thread)
                            return thread;
                        // A reconnect replays from the cursor, and a replay may
                        // overlap by one — `seq` is what settles it, as it settles
                        // the ordering everywhere else.
                        if (thread.messages.some((held) => held.seq === message.seq))
                            return thread;
                        return {
                            ...thread,
                            messages: [...thread.messages, message],
                            messageSeq: Math.max(thread.messageSeq, message.seq),
                            // Mine came back from my own send; theirs is the thing this
                            // count exists to describe.
                            unreadCount: message.isMine ? thread.unreadCount : thread.unreadCount + 1,
                        };
                    });
                },
                onRevise: (message) => {
                    queryClient.setQueryData(key, (thread) => thread ? replaceMessage(thread, message) : thread);
                },
                onClose: () => {
                    close = null;
                    if (!live)
                        return;
                    /*
                      The stream's revision cursor starts fresh on each connection,
                      so an edit or a delete made while this one was down would
                      never arrive. One re-read of the thread covers it.
                    */
                    reconnected = true;
                    retry = setTimeout(open, wait);
                    wait = Math.min(wait * 2, 60_000);
                },
            }, cursor.current);
        };
        open();
        return () => {
            live = false;
            if (retry)
                clearTimeout(retry);
            close?.();
        };
        /*
          `id` and not the key built from it. `queryKeys.thread(id)` is a fresh
          array on every render, so naming it here meant `Object.is` never
          matched and the effect tore the stream down and opened a new one on
          *every* re-render of the screen that calls this — and the composer's
          draft lives in that same component, so a ten-character reply aborted
          and reopened the event stream ten times. Each reopen replays from the
          cursor and takes another listener slot, and the overlapping
          subscribes cross `MAX_STREAMS_PER_THREAD`, at which point the hub
          refuses and the conversation falls into the reconnect backoff while
          the person is still typing.
        */
    }, [client, id, ready, queryClient]);
    useEffect(() => {
        if (!id || unread === 0)
            return;
        void client.markThreadRead(id, seq).then(() => {
            // The badge is derived from the same cursor, so it has to be asked
            // again rather than left showing a number that is no longer true.
            void queryClient.invalidateQueries({ queryKey: queryKeys.unreadMessages() });
            void queryClient.invalidateQueries({ queryKey: ['threads'] });
        });
    }, [client, id, seq, unread, queryClient]);
    return query;
}
/**
 * Sends, and refetches rather than guessing.
 *
 * No optimistic append: the server decides the sequence number, and it may
 * also decide the body — a message with a phone number in it comes back
 * masked, and showing the sender their unmasked text until the refetch
 * would be telling them their number went through when it did not.
 */
export function useSendMessage(client, threadId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.sendMessage(threadId, input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.thread(threadId ?? '') });
            void queryClient.invalidateQueries({ queryKey: ['threads'] });
        },
    });
}
/** The thread with one message swapped for its newer self, matched by id. */
function replaceMessage(thread, message) {
    if (!thread.messages.some((held) => held.id === message.id))
        return thread;
    return {
        ...thread,
        messages: thread.messages.map((held) => (held.id === message.id ? message : held)),
    };
}
/**
 * Edits one of the caller's own messages.
 *
 * The answer is written into the thread rather than guessed ahead of it,
 * for the reason `useSendMessage` does not append optimistically: the
 * server may mask what was typed. The inbox is asked again because the
 * edited message may be the preview on its row.
 */
export function useEditMessage(client, threadId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ messageId, ...input }) => client.editMessage(threadId, messageId, input),
        onSuccess: (message) => {
            queryClient.setQueryData(queryKeys.thread(threadId ?? ''), (thread) => thread ? replaceMessage(thread, message) : thread);
            void queryClient.invalidateQueries({ queryKey: ['threads'] });
        },
    });
}
/** Withdraws one of the caller's own messages. See `useEditMessage`. */
export function useDeleteMessage(client, threadId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (messageId) => client.deleteMessage(threadId, messageId),
        onSuccess: (message) => {
            queryClient.setQueryData(queryKeys.thread(threadId ?? ''), (thread) => thread ? replaceMessage(thread, message) : thread);
            void queryClient.invalidateQueries({ queryKey: ['threads'] });
        },
    });
}
/**
 * Mute or unmute one thread from the inbox. Both keys are asked again: the
 * row draws the muted mark, and the thread's own head may be open behind.
 */
export function useMuteThread(client) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, muted }) => client.muteThread(id, muted),
        onSuccess: (_, { id }) => {
            void queryClient.invalidateQueries({ queryKey: ['threads'] });
            void queryClient.invalidateQueries({ queryKey: queryKeys.thread(id) });
        },
    });
}
/**
 * Mark a whole thread read without opening it — the inbox's long press.
 *
 * To the thread's own high-water mark, which the row already holds. The
 * badge is derived from the same cursor, so it is asked again too.
 */
export function useMarkThreadRead(client) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, seq }) => client.markThreadRead(id, seq),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.unreadMessages() });
            void queryClient.invalidateQueries({ queryKey: ['threads'] });
        },
    });
}
/**
 * Opens the conversation these two have, or reopens the one they already
 * had, and hands back the thread to navigate to.
 *
 * Safe to fire from a Message button somebody taps twice: a customer and a
 * partner have one thread however many times they do business. Invalidates
 * the inbox because an opened thread is a new row in it — and only the
 * inbox, because the thread itself came back in the answer.
 */
export function useOpenThread(client) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.openThread(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['threads'] });
        },
    });
}
/**
 * Status, assignee, priority, topic, tags — an agent triaging one thread.
 *
 * Both keys are invalidated because both are wrong afterwards: the header
 * of the thread on screen, and the row for it in the inbox behind. Two
 * dashboards hand-rolled this mutation and each remembered one of them,
 * which is how a thread reads as assigned on its own page and unassigned
 * in the list it was assigned from.
 */
export function useUpdateThread(client, id) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (patch) => client.updateThread(id, patch),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.thread(id ?? '') });
            void queryClient.invalidateQueries({ queryKey: ['threads'] });
        },
    });
}
/**
 * The same change across a selection.
 *
 * It resolves rather than throwing when some of them refuse — the answer
 * says which took — so the caller reads `results` instead of `error`, and
 * the refetch below is what settles the disagreement either way. `thread`
 * goes too, as a prefix: any of the selected may be open in another tab.
 */
export function useBulkUpdateThreads(client) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.bulkUpdateThreads(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['threads'] });
            void queryClient.invalidateQueries({ queryKey: ['thread'] });
        },
    });
}
/**
 * Colleagues, for the assignee picker and for putting names on a report.
 *
 * Held for five minutes: a staff list changes when somebody is hired, and
 * an inbox that refetched it with every thread would be asking a question
 * whose answer is the same all quarter.
 */
export function useStaff(client, query = {}) {
    return useQuery({
        queryKey: queryKeys.staff(query),
        queryFn: () => client.listStaff(query),
        staleTime: 5 * 60_000,
    });
}
/** The canned answers for this thread's language and topic. Just as static. */
export function useSavedReplies(client, query = {}) {
    return useQuery({
        queryKey: queryKeys.savedReplies(query),
        queryFn: () => client.listSavedReplies(query),
        staleTime: 5 * 60_000,
    });
}
/**
 * The reporting page's numbers.
 *
 * No interval. A report is a window somebody chose and read, not a thing
 * that has to keep up — and these are the most expensive reads on the API,
 * a scan of the range rather than a page of rows.
 */
export function useThreadMetrics(client, range = {}) {
    return useQuery({
        queryKey: queryKeys.threadMetrics(range),
        queryFn: () => client.threadMetrics(range),
        staleTime: 60_000,
    });
}
/**
 * The score the requester gives, once, from the prompt on a finished
 * thread.
 *
 * The thread is invalidated rather than patched in place, because what
 * dismisses the prompt is the `csat` that comes back on the thread — and a
 * client that hid it on its own would show it again on the next read.
 */
export function useSubmitCsat(client, id) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input) => client.submitCsat(id, input),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.thread(id ?? '') });
            void queryClient.invalidateQueries({ queryKey: ['threads'] });
        },
    });
}
/**
 * The full-size picture, asked for at the moment somebody opens it.
 *
 * `enabled` is off unless a caller says otherwise, and that is the whole
 * design: a thread of thirty photographs must not fire thirty of these on
 * open. What a list draws is the thumbnail that came down with the thread.
 *
 * `staleTime` sits under the URL's own minute, so the answer is replaced
 * before the signature inside it dies.
 */
export function useAttachmentLink(client, id, enabled = false) {
    return useQuery({
        queryKey: queryKeys.attachmentLink(id ?? ''),
        queryFn: () => client.attachmentLink(id),
        enabled: enabled && Boolean(id),
        staleTime: 30_000,
        gcTime: 60_000,
    });
}
export function useVehicles(client, query = {}, options) {
    return useQuery({
        queryKey: queryKeys.vehicles(query),
        queryFn: () => client.listVehicles(query),
        ...options,
    });
}
export function useVehicle(client, idOrSlug) {
    return useQuery({
        queryKey: queryKeys.vehicle(idOrSlug),
        queryFn: () => client.getVehicle(idOrSlug),
        enabled: Boolean(idOrSlug),
    });
}
export function useBookings(client, query = {}) {
    return useQuery({
        queryKey: queryKeys.bookings(query),
        queryFn: () => client.listBookings(query),
    });
}
export function useQuote(client) {
    return useMutation({ mutationFn: (input) => client.quote(input) });
}
export function useCreateBooking(client) {
    return useMutation({
        mutationFn: (input) => client.createBooking(input),
    });
}
export * from './flags-react.js';
